import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackEvent } from "../lib/track";
import { markBlueprintStarted, markBlueprintSubmitted } from "../lib/conversionState";
import { BLUEPRINT_SECTIONS } from "../data/blueprintQuestionnaire";

const LOGO = "/assets/logo.png";

const getSessionId = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("session_id");
};

const storageKey = (sid) => `am-blueprint-${sid}`;

const blankAnswers = () =>
  BLUEPRINT_SECTIONS.reduce((acc, s) => {
    acc[s.id] = s.questions.reduce((q, x) => {
      q[x.id] = "";
      return q;
    }, {});
    return acc;
  }, {});

const Field = ({ q, value, onChange }) => (
  <div>
    <label htmlFor={q.id} className="block text-sm text-themed leading-relaxed mb-2">
      {q.label}
    </label>
    {q.short ? (
      <input
        id={q.id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={q.placeholder || ""}
        className="input-bare"
      />
    ) : (
      <textarea
        id={q.id}
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-bare resize-none"
        placeholder="Answer honestly. There is no right length."
      />
    )}
  </div>
);

const SectionForm = ({ section, values, onChange }) => (
  <div className="glass-card p-8 sm:p-10">
    <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">
      Section {section.index + 1} of {BLUEPRINT_SECTIONS.length}
    </p>
    <h2 className="font-serif text-3xl sm:text-4xl text-themed leading-tight font-light">
      {section.title}
    </h2>
    <p className="mt-3 text-themed-muted text-sm leading-relaxed">{section.explanation}</p>
    <div className="mt-8 space-y-7">
      {section.questions.map((q) => (
        <Field
          key={q.id}
          q={q}
          value={values[q.id] || ""}
          onChange={(v) => onChange(q.id, v)}
        />
      ))}
    </div>
  </div>
);

const ProgressBar = ({ current, total }) => (
  <div className="flex items-center gap-3 mb-8">
    <div className="flex-grow h-[2px] bg-white/10 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-[#997A15] via-[#D4AF37] to-[#F3E5AB] transition-all duration-500"
        style={{ width: `${((current + 1) / total) * 100}%` }}
      />
    </div>
    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/70 shrink-0">
      {current + 1} / {total}
    </span>
  </div>
);

const Locked = ({ reason }) => (
  <div className="max-w-xl mx-auto text-center py-20">
    <h1 className="font-serif text-4xl text-themed leading-tight font-light">
      This Personal Action Plan is locked.
    </h1>
    <p className="mt-6 text-themed-muted leading-relaxed">{reason}</p>
    <a href="/?checkout=blueprint" className="btn-ghost-gold mt-8 inline-block">Return to Personal Action Plan</a>
  </div>
);

const BlueprintIntakePage = () => {
  const sessionId = getSessionId();
  const [accessState, setAccessState] = useState({ status: "checking", reason: "" });
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(blankAnswers);
  const [submitting, setSubmitting] = useState(false);

  // Verify session
  useEffect(() => {
    if (!sessionId) {
      setAccessState({ status: "locked", reason: "A purchase confirmation link is required." });
      return;
    }
    api.get(`/blueprint/submissions/check/${encodeURIComponent(sessionId)}`)
      .then((r) => {
        if (r.data?.submitted) {
          window.location.href = `/blueprint/preview?session_id=${encodeURIComponent(sessionId)}`;
          return;
        }
        setAccessState({ status: "ready", reason: "" });
        trackEvent("blueprint_started", { session_id: sessionId });
        markBlueprintStarted();
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setAccessState({
          status: "locked",
          reason: detail || "We could not verify this purchase yet. Your access is protected, not lost.",
        });
      });
  }, [sessionId]);

  // Restore draft from localStorage
  useEffect(() => {
    if (!sessionId || accessState.status !== "ready") return;
    try {
      const raw = localStorage.getItem(storageKey(sessionId));
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.answers) setAnswers((a) => ({ ...a, ...saved.answers }));
        if (typeof saved.step === "number") setStep(saved.step);
      }
    } catch {
      /* ignore corrupt draft */
    }
  }, [sessionId, accessState.status]);

  // Persist draft
  useEffect(() => {
    if (!sessionId || accessState.status !== "ready") return;
    try {
      localStorage.setItem(storageKey(sessionId), JSON.stringify({ answers, step }));
    } catch {
      /* ignore */
    }
  }, [sessionId, answers, step, accessState.status]);

  const sectionView = useMemo(() => {
    const s = BLUEPRINT_SECTIONS[step];
    return { ...s, index: step };
  }, [step]);

  const setAnswer = (sectionId) => (qid, val) => {
    setAnswers((a) => ({ ...a, [sectionId]: { ...a[sectionId], [qid]: val } }));
  };

  const goNext = () => setStep((s) => Math.min(s + 1, BLUEPRINT_SECTIONS.length));
  const goPrev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    try {
      setSubmitting(true);
      const name = answers.identity_signal?.name_on_blueprint || "";
      await api.post("/blueprint/submissions", {
        stripe_session_id: sessionId,
        customer_name: name || undefined,
        answers,
      });
      trackEvent("blueprint_submitted", { session_id: sessionId });
      markBlueprintSubmitted();
      try { localStorage.removeItem(storageKey(sessionId)); } catch { /* ignore */ }
      window.location.href = `/blueprint/preview?session_id=${encodeURIComponent(sessionId)}`;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please try once more.";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  if (accessState.status === "checking") {
    return (
      <div className="min-h-screen bg-themed text-themed flex items-center justify-center">
        <p className="font-serif text-2xl text-themed-muted">Confirming your alignment…</p>
      </div>
    );
  }
  if (accessState.status === "locked") {
    return (
      <div className="min-h-screen bg-themed text-themed relative">
        <div className="absolute inset-0 gold-glow-radial pointer-events-none" />
        <Locked reason={accessState.reason} />
      </div>
    );
  }

  const onReview = step === BLUEPRINT_SECTIONS.length;
  const currentSectionId = onReview ? null : BLUEPRINT_SECTIONS[step].id;

  return (
    <div data-testid="blueprint-intake-page" className="min-h-screen bg-themed text-themed relative overflow-hidden pb-32">
      <div className="absolute inset-0 gold-glow-radial pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <header className="relative pt-12 pb-8 text-center px-6">
        <div className="flex justify-center mb-6">
          <img src={LOGO} alt="Authentic Momentum" className="w-16 h-16 object-contain logo-mask" draggable="false" />
        </div>
        <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-3">Personal Action Plan Intake</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-themed leading-tight font-light">
          Begin Your Authentic Momentum Personal Action Plan
        </h1>
        <p className="mt-5 text-themed-muted max-w-2xl mx-auto leading-relaxed">
          This questionnaire gathers the raw material for your personal Personal Action Plan. Answer with honesty, not performance. The more real the answers, the clearer the synthesis can become.
        </p>
        <p className="mt-3 text-themed-muted text-sm max-w-2xl mx-auto leading-relaxed">
          After you submit, Cyrus will review your answers and use them to synthesize your complete Personal Action Plan. You will receive a personalized reflection of your identity, energy, story, direction, and next aligned move.
        </p>
        <p className="mt-3 text-[10px] tracking-[0.3em] uppercase text-gold/60">
          This is not an automated diagnosis. This is a guided clarity process.
        </p>
      </header>

      <main className="relative max-w-3xl mx-auto px-6">
        <ProgressBar current={onReview ? BLUEPRINT_SECTIONS.length - 1 : step} total={BLUEPRINT_SECTIONS.length} />

        {!onReview && (
          <SectionForm
            section={sectionView}
            values={answers[currentSectionId] || {}}
            onChange={setAnswer(currentSectionId)}
          />
        )}

        {onReview && (
          <div className="glass-card p-8 sm:p-10" data-testid="blueprint-review">
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">Final Review</p>
            <h2 className="font-serif text-3xl text-themed leading-tight font-light">
              Submit your reflection for synthesis.
            </h2>
            <p className="mt-4 text-themed-muted leading-relaxed">
              When you submit, your answers are sent privately to Cyrus. He will review them and synthesize your complete Personal Action Plan, then return it to you.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-themed-muted">
              {BLUEPRINT_SECTIONS.map((s) => {
                const answered = Object.values(answers[s.id] || {}).filter(Boolean).length;
                return (
                  <li key={s.id} className="flex items-start gap-3">
                    <Check size={14} className="text-gold mt-1 shrink-0" />
                    <span>
                      <span className="text-themed">{s.title}</span>{" "}
                      <span className="text-themed-muted text-xs">· {answered} of {s.questions.length} answered</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
          <button
            type="button"
            onClick={goPrev}
            disabled={step === 0}
            data-testid="blueprint-prev"
            className="btn-ghost-gold inline-flex items-center gap-2 disabled:opacity-30"
          >
            <ArrowLeft size={13} /> Back
          </button>
          {!onReview ? (
            <button
              type="button"
              onClick={goNext}
              data-testid="blueprint-next"
              className="btn-gold inline-flex items-center gap-2"
            >
              Next <ArrowRight size={13} />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="blueprint-submit"
              className="btn-gold inline-flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit for Synthesis"} <ArrowRight size={13} />
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default BlueprintIntakePage;
